import { useMemo, useState } from "react";
import { categoryFilters, hotTopics } from "@/mocks/guaData";

const tagColor: Record<string, string> = {
  全部: "bg-secondary-100 text-secondary-800",
  综艺: "bg-primary-100 text-primary-700",
  恋综: "bg-accent-100 text-accent-800",
  剧组: "bg-secondary-100 text-secondary-800",
  职场: "bg-primary-50 text-primary-700",
  生活: "bg-accent-50 text-accent-700",
  数码: "bg-secondary-100 text-secondary-800",
};

function heatLabel(v: number) {
  return v >= 1000000 ? `${(v / 10000).toFixed(1)} 万` : `${(v / 1000).toFixed(1)} 千`;
}

export default function HotTopics() {
  const [active, setActive] = useState("全部");
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    return hotTopics.filter((t) => {
      const okTag = active === "全部" || t.tag === active;
      const okQ =
        !query.trim() ||
        t.title.includes(query.trim()) ||
        t.excerpt.includes(query.trim());
      return okTag && okQ;
    });
  }, [active, query]);

  return (
    <section id="today" className="bg-background-100/60 py-12 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-medium text-primary-600">
              <span className="inline-block h-4 w-1 rounded-full bg-primary-500"></span>
              今日热点
            </p>
            <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              大家最近都在看什么
            </h2>
            <p className="mt-2 text-sm text-foreground-600 md:text-base">
              按热度实时排序，点开就能看到大家怎么看、热议什么。
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="话题分类">
              {categoryFilters.map((c) => (
                <button
                  key={c}
                  role="tab"
                  aria-selected={active === c}
                  onClick={() => setActive(c)}
                  className={
                    active === c
                      ? "whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm font-medium text-background-50 transition"
                      : "whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm font-medium text-foreground-700 transition hover:border-primary-300 hover:text-primary-700"
                  }
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜热点话题"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 sm:w-56"
                aria-label="搜索热点话题"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
              <p className="text-sm text-foreground-500">没有找到相关话题，换个关键词试试吧</p>
            </div>
          )}
          {list.map((t) => (
            <article
              key={t.id}
              className="group flex flex-col justify-between rounded-xl border border-background-200 bg-background-50 p-5 transition hover:-translate-y-0.5 hover:border-primary-200"
            >
              <div>
                <div className="flex items-center gap-2">
                  <span
                    className={`whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${tagColor[t.tag]}`}
                  >
                    {t.tag}
                  </span>
                  {t.isLive && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600">
                      <span className="live-dot h-1.5 w-1.5 rounded-full bg-primary-500"></span>
                      现场
                    </span>
                  )}
                  {t.badge && (
                    <span className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded bg-accent-500 text-xs font-bold text-background-50">
                      {t.badge}
                    </span>
                  )}
                </div>
                <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-700">
                  {t.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">
                  {t.excerpt}
                </p>
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3">
                <div className="flex items-center gap-2 text-xs text-foreground-500">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-[10px] font-bold text-primary-700">
                    {t.author[0]}
                  </span>
                  <span className="whitespace-nowrap">{t.author}</span>
                  <span className="whitespace-nowrap">· {t.time}</span>
                </div>
                <div className="flex items-center gap-2.5 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-fire-line text-primary-500"></i>
                    {heatLabel(t.heat)}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {t.replies}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}